import React, { useState, useRef, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Pencil, Smartphone, Monitor, Play, Pause, Users, LogOut, Lock } from "lucide-react";
import { toast } from "sonner";
import { useInvitation } from "../context/InvitationContext";
import { useAuth } from "../context/AuthContext";
import { Envelope } from "../components/Envelope";
import { InvitationView } from "../components/InvitationView";
import { MusicPlayer } from "../components/MusicPlayer";
import { EditorPanel } from "../components/EditorPanel";

export default function Home() {
  const { data, loading, updateField, save, API, eventKey, eventBase } = useInvitation();
  const { isPublisher, logout } = useAuth();
  const { slug } = useParams();
  const [invitee, setInvitee] = useState(null);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (!slug) { setInvitee(null); return; }
    axios.get(`${API}/invites/${slug}?eventKey=${eventKey}`)
      .then((res) => setInvitee(res.data))
      .catch(() => setInvitee(null));
  }, [slug, API, eventKey]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [autoScrolling, setAutoScrolling] = useState(false);
  const rafRef = useRef(null);

  // Smooth continuous auto-scroll glide down the invitation
  useEffect(() => {
    if (!autoScrolling) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    let last = performance.now();
    const speed = { slow: 32, medium: 60, fast: 105 }[data?.autoScrollSpeed] || 60; // px per second
    const step = (now) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      const maxY = document.documentElement.scrollHeight - window.innerHeight;
      if (window.scrollY >= maxY - 2) {
        setAutoScrolling(false);
        return;
      }
      window.scrollBy(0, speed * dt);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [autoScrolling, data?.autoScrollSpeed]);

  // Pause auto-scroll on manual interaction
  useEffect(() => {
    const pause = () => setAutoScrolling(false);
    window.addEventListener("wheel", pause, { passive: true });
    window.addEventListener("touchmove", pause, { passive: true });
    window.addEventListener("keydown", pause);
    return () => {
      window.removeEventListener("wheel", pause);
      window.removeEventListener("touchmove", pause);
      window.removeEventListener("keydown", pause);
    };
  }, []);

  // Auto-start the glide shortly after the envelope opens
  useEffect(() => {
    if (opened) {
      if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
      window.scrollTo(0, 0);
    }
    if (opened && data?.autoScroll) {
      const t = setTimeout(() => setAutoScrolling(true), 2200);
      return () => clearTimeout(t);
    }
  }, [opened, data?.autoScroll]);

  const toggleAutoScroll = useCallback(() => {
    if (!autoScrolling && window.scrollY >= document.documentElement.scrollHeight - window.innerHeight - 4) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => setAutoScrolling(true), 600);
      return;
    }
    setAutoScrolling((s) => !s);
  }, [autoScrolling]);

  if (loading || !data)
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FBEEF0" }}>
        <span className="font-display text-2xl" style={{ color: "#5C162E" }}>Loading…</span>
      </div>
    );

  const c = data.colors;
  const phoneMode = !!data.phoneMode;

  const toggleView = async () => {
    const next = { ...data, phoneMode: !phoneMode };
    updateField("phoneMode", !phoneMode);
    await save(next);
    toast.success(!phoneMode ? "Portrait phone view" : "Full-width view");
  };

  const stage = (
    <div
      className={
        phoneMode
          ? "relative min-h-screen w-full sm:w-[420px] sm:my-8 sm:min-h-[860px] sm:rounded-[2.2rem] sm:border-[10px] overflow-hidden shadow-2xl"
          : "relative min-h-screen w-full overflow-hidden"
      }
      style={phoneMode ? { borderColor: "#12060a", background: c.bg } : { background: c.bg }}
      data-testid="invitation-stage"
    >
      <AnimatePresence mode="wait">
        {!opened && <Envelope key="env" data={data} onOpen={() => { window.scrollTo(0, 0); setOpened(true); }} />}
      </AnimatePresence>
      {opened && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9 }}>
          <InvitationView data={data} API={API} invitee={invitee} eventKey={eventKey} />
        </motion.div>
      )}
    </div>
  );

  return (
    <div
      className="relative min-h-screen"
      style={{ "--font-display": `'${data.fontDisplay || "Cormorant Garamond"}'`, "--font-names": `'${data.fontNames || "Cormorant Garamond"}'`, "--font-monogram": `'${data.fontMonogram || "Cormorant Garamond"}'`, "--font-venue": `'${data.fontVenue || "Cormorant Garamond"}'` }}
    >
      {phoneMode && (
        <div
          className="fixed inset-0 z-0"
          style={{ background: `radial-gradient(circle at 50% 15%, ${c.primary}22, ${c.primary}66)` }}
        />
      )}

      <div className={phoneMode ? "relative z-10 min-h-screen flex justify-center" : "relative z-10"}>
        {stage}
      </div>

      {opened && <MusicPlayer url={data.musicUrl} colors={c} autoStart={opened} />}

      {/* Auto-scroll play/pause */}
      {opened && (
        <motion.button
          data-testid="auto-scroll-btn"
          onClick={toggleAutoScroll}
          className="fixed bottom-[8.2rem] left-5 z-50 flex items-center gap-2 rounded-full pl-3 pr-4 py-2 shadow-lg backdrop-blur-xl"
          style={{ background: "rgba(255,255,255,0.85)", color: c.primary, border: `1px solid ${c.primary}22` }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {autoScrolling ? <Pause size={15} /> : <Play size={15} />}
          <span className="text-[11px] font-body tracking-wide">{autoScrolling ? "Pause" : "Auto-play"}</span>
        </motion.button>
      )}

      {/* View mode toggle */}
      {isPublisher && (
      <motion.button
        data-testid="toggle-view-btn"
        onClick={toggleView}
        className="fixed bottom-[4.6rem] left-5 z-50 flex items-center gap-2 rounded-full pl-3 pr-4 py-2 shadow-lg backdrop-blur-xl"
        style={{ background: "rgba(255,255,255,0.85)", color: c.primary, border: `1px solid ${c.primary}22` }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {phoneMode ? <Monitor size={15} /> : <Smartphone size={15} />}
        <span className="text-[11px] font-body tracking-wide">{phoneMode ? "Full view" : "Phone view"}</span>
      </motion.button>
      )}

      {/* Editor toggle (publisher only) */}
      {isPublisher && (
      <motion.button
        data-testid="open-editor-btn"
        onClick={() => setEditorOpen(true)}
        className="fixed bottom-6 left-5 z-50 flex items-center gap-2 rounded-full pl-3 pr-4 py-2.5 shadow-lg backdrop-blur-xl"
        style={{ background: "rgba(255,255,255,0.85)", color: c.primary, border: `1px solid ${c.primary}22` }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Pencil size={16} />
        <span className="text-xs font-body tracking-wide">Edit</span>
      </motion.button>
      )}

      {/* Publisher utilities: guest list + logout */}
      {isPublisher && (
        <div className="fixed top-5 right-5 z-50 flex items-center gap-2">
          <motion.a
            href={`${eventBase}/guests`}
            data-testid="guests-link-btn"
            className="flex items-center gap-2 rounded-full pl-3 pr-4 py-2 shadow-lg backdrop-blur-xl"
            style={{ background: "rgba(255,255,255,0.85)", color: c.primary, border: `1px solid ${c.primary}22` }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Users size={15} />
            <span className="text-[11px] font-body tracking-wide">Guests</span>
          </motion.a>
          <motion.button
            data-testid="home-logout-btn"
            onClick={logout}
            className="flex items-center gap-2 rounded-full px-3 py-2 shadow-lg backdrop-blur-xl"
            style={{ background: "rgba(255,255,255,0.85)", color: c.primary, border: `1px solid ${c.primary}22` }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <LogOut size={15} />
          </motion.button>
        </div>
      )}

      {isPublisher && <EditorPanel open={editorOpen} onOpenChange={setEditorOpen} />}

      {/* Discreet publisher sign-in (only when not logged in) */}
      {opened && !isPublisher && (
        <motion.a
          href="/login"
          data-testid="publisher-login-link"
          title="Publisher login"
          className="fixed top-5 right-5 z-50 flex items-center justify-center w-10 h-10 rounded-full shadow-lg backdrop-blur-xl"
          style={{ background: "rgba(255,255,255,0.7)", color: c.primary, border: `1px solid ${c.primary}22` }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.55 }}
          whileHover={{ opacity: 1, scale: 1.08 }}
        >
          <Lock size={15} />
        </motion.a>
      )}
    </div>
  );
}
